/**
 * useAccountUIState Hook
 * 
 * Manages UI state for the Accounts page:
 * - Expanded account details
 * - Snackbar state
 */

import { useState, useEffect } from "react";
import type React from "react";
import { ValuesVisibilityMode } from "../../../types";

interface UseAccountUIStateParams {
  workspaceId: string | undefined;
  valuesVisibilityMode: ValuesVisibilityMode;
  valuesVisible: boolean;
  setValuesVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

interface UseAccountUIStateReturn {
  expandedAccountDetails: Set<string>;
  setExpandedAccountDetails: React.Dispatch<React.SetStateAction<Set<string>>>;
  snackbarOpen: boolean;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  snackbarMessage: string;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  snackbarElement: React.ReactElement | null;
  setSnackbarElement: React.Dispatch<React.SetStateAction<React.ReactElement | null>>;
  toggleAccountDetails: (accountId: string) => void;
  toggleValuesVisibility: () => void;
}

/**
 * Hook to manage UI state for Accounts page
 */
export function useAccountUIState({
  workspaceId,
  valuesVisibilityMode,
  valuesVisible,
  setValuesVisible,
}: UseAccountUIStateParams): UseAccountUIStateReturn {
  const [expandedAccountDetails, setExpandedAccountDetails] = useState<Set<string>>(new Set());
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarElement, setSnackbarElement] = useState<React.ReactElement | null>(null);

  // Reset visibility based on mode when mode changes
  useEffect(() => {
    if (valuesVisibilityMode === "always-visible") {
      // Always-visible mode: force visible and clear localStorage
      setValuesVisible(true);
      if (workspaceId) {
        localStorage.removeItem(`valuesVisible_${workspaceId}`);
      }
    } else if (valuesVisibilityMode === "always-closed") {
      // Always-closed mode: always start with hidden (reset to hidden)
      setValuesVisible(false);
      // Don't save to localStorage, always default to hidden
    }
    // Manual mode: keep current state (handled in toggle)
  }, [valuesVisibilityMode, workspaceId, setValuesVisible]);

  // Toggle account details expansion
  const toggleAccountDetails = (accountId: string): void => {
    setExpandedAccountDetails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  // Toggle values visibility
  const toggleValuesVisibility = (): void => {
    setValuesVisible((prev) => {
      const newValue = !prev;
      
      // Save to localStorage only for manual mode
      if (valuesVisibilityMode === "manual" && workspaceId) {
        // Manual mode: save user preference to localStorage
        localStorage.setItem(`valuesVisible_${workspaceId}`, String(newValue));
      }
      // For "always-closed" and "always-visible" modes, don't save to localStorage
      // They will reset to their default state on page refresh/navigation
      
      return newValue;
    });
  };

  return {
    expandedAccountDetails,
    setExpandedAccountDetails,
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    setSnackbarMessage,
    snackbarElement,
    setSnackbarElement,
    toggleAccountDetails,
    toggleValuesVisibility,
  };
}
